import { next } from '@ember/runloop';
import EmberService from '@ember/service';

import type { EventData } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { isAlive } from 'webapp/utils/common';

type Callback<N extends EventData.Name> = (data: EventData.Payload<N>) => boolean | void; // true unbinds it

interface Subscription {
	context: object;
	callback: (data: unknown) => boolean | void;
}

type Subscriptions = Record<string, Subscription[]>; // key is eventName

// event name of the reply to an async request (triggered on its bgEvent, see socket triggerBgEventDone)
export function asyncReplyEventName(
	origin: string,
	requestId: string | number | undefined,
): EventData.AsyncReplyName {
	return `${origin}${requestId}` as EventData.AsyncReplyName;
}

// event name of a master date range change of one report board fragment
export function reportDataboardsMDRChangedEventName(
	fragmentId: string | null,
): EventData.ReportDataboardsMDRChangedName {
	return `${AppEvent.REPORT_DATABOARDS_MDR_CHANGED}${fragmentId}`;
}

export default class EventManagerService extends EmberService {
	private subscriptions: Subscriptions = {};

	on<N extends EventData.Name>(eventName: N, context: object, callback: Callback<N>): void {
		const subscriptionsForEvent = this.subscriptions[eventName] || [];

		subscriptionsForEvent.push({
			context,
			callback: callback as Subscription['callback'],
		});

		this.subscriptions[eventName] = subscriptionsForEvent;
	}

	off(eventName: EventData.Name, context: object): void {
		if (!this.subscriptions[eventName]) {
			return;
		}

		this.subscriptions[eventName] = this.subscriptions[eventName].filter(
			(item) => item.context !== context,
		);

		if (!this.subscriptions[eventName].length) {
			delete this.subscriptions[eventName];
		}
	}

	// the payload is required when the event has one
	trigger<N extends EventData.Name>(
		eventName: N,
		...[eventData]: EventData.Payload<N> extends undefined ? [] : [EventData.Payload<N>]
	): boolean {
		const subscriptions = this.subscriptions[eventName] || [];

		subscriptions.forEach((subscription) => {
			let unbindEvent = false;

			if (isAlive(subscription.context)) {
				unbindEvent = !!subscription.callback.call(subscription.context, eventData);
			} else {
				// remove events whose context is destroyed
				unbindEvent = true;
			}

			if (unbindEvent) {
				next(this, this.off, eventName, subscription.context);
			}
		});

		return subscriptions.length > 0; // returning if any event was triggered at all
	}
}

declare module '@ember/service' {
	interface Registry {
		'event-manager': EventManagerService;
	}
}
